@let persona = currentPersona();

<div class="flex flex-col h-screen overflow-hidden print:h-auto print:overflow-visible print:block">
  <!-- TOP BAR (navy) — logo, wordmark, account avatar -->
  <header class="bg-navy text-white border-b border-navy-deep shrink-0 print:hidden">
    <div class="flex items-center gap-6 px-6 py-3">
      <img src="assets/dr-logo.png" alt="Diamond Ridge" class="h-9 w-auto shrink-0" />

      <div class="leading-tight">
        <div class="type-h3">DIAMOND RIDGE</div>
        <div class="type-label text-white/60" style="letter-spacing: 0.2em">
          Development Group · DROS
        </div>
      </div>

      <div class="ml-auto flex items-center gap-4">
        <i class="pi pi-envelope text-white/60" title="Messages"></i>

        <!-- NOTIFICATIONS BELL — button with an unread-count badge and a dropdown
             panel. Wrapper is `relative` so the panel can anchor beneath the bell. -->
        <div class="relative">
          <button
            type="button"
            (click)="toggleBell()"
            title="Notifications"
            class="relative flex items-center text-white/60 hover:text-white transition-colors"
          >
            <i class="pi pi-bell"></i>
            @if (unreadCount() > 0) {
              <!-- Unread count: mirrors the sidebar's rose badge, shrunk to a dot at
                   the bell's top-right corner. -->
              <span
                class="absolute -top-2 -right-2 number type-meta font-semibold bg-rose-600 text-white min-w-4 h-4 px-1 flex items-center justify-center rounded-full"
                [title]="unreadCount() + ' unread'"
              >{{ unreadCount() }}</span>
            }
          </button>

          @if (bellOpen()) {
            <!-- Bell opens a modal of the MOST RECENT alerts, with "View all" → the
                 full /alerts history. Opening marked them read (see toggleBell). -->
            <dros-modal title="Notifications" maxWidth="max-w-md" align="top" (closed)="closeBell()">
              @if (recentNotifications().length === 0) {
                <div class="py-6 type-meta text-slate-500 text-center">You're all caught up.</div>
              } @else {
                <div class="divide-y divide-slate-100">
                  @for (n of recentNotifications(); track n.id) {
                    @if (n.link) {
                      <!-- Linked alert: the whole row navigates and closes the modal. -->
                      <a [routerLink]="n.link" (click)="closeBell()" class="flex gap-2 py-3 hover:bg-slate-50 transition-colors">
                        <span class="mt-1 w-2 h-2 rounded-full shrink-0" [class]="n.read ? 'bg-transparent' : 'bg-rose-600'"></span>
                        <div class="min-w-0 flex-1">
                          <div class="flex items-baseline gap-2">
                            <div class="type-meta font-semibold text-slate-900 truncate">{{ n.title }}</div>
                            <div class="ml-auto type-meta text-slate-400 shrink-0">{{ shortTime(n.createdAt) }}</div>
                          </div>
                          <div class="type-meta text-slate-500 leading-snug">{{ n.message }}</div>
                        </div>
                      </a>
                    } @else {
                      <div class="flex gap-2 py-3">
                        <span class="mt-1 w-2 h-2 rounded-full shrink-0" [class]="n.read ? 'bg-transparent' : 'bg-rose-600'"></span>
                        <div class="min-w-0 flex-1">
                          <div class="flex items-baseline gap-2">
                            <div class="type-meta font-semibold text-slate-900 truncate">{{ n.title }}</div>
                            <div class="ml-auto type-meta text-slate-400 shrink-0">{{ shortTime(n.createdAt) }}</div>
                          </div>
                          <div class="type-meta text-slate-500 leading-snug">{{ n.message }}</div>
                        </div>
                      </div>
                    }
                  }
                </div>
              }
              <div footer class="px-5 py-3 border-t border-slate-200 flex justify-between items-center">
                <span class="type-meta text-slate-400">Your most recent</span>
                <a routerLink="/alerts" (click)="closeBell()" class="type-body bg-navy text-white px-4 py-2 hover:bg-navy-deep">View all</a>
              </div>
            </dros-modal>
          }
        </div>

        @if (persona) {
          <a
            routerLink="/account"
            title="Account Settings"
            class="flex items-center gap-3 pl-4 border-l border-white/20 hover:bg-white/5 -my-2 py-2 px-3 -mr-3 transition-colors"
          >
            <div class="text-right leading-tight">
              <div class="font-semibold type-body">{{ persona.name }}</div>
              <div class="type-meta text-white/60">{{ persona.title }}</div>
            </div>
            <dros-avatar [name]="persona.name" [photoUrl]="persona.photoUrl" [size]="36" tile="bg-white/15 text-white" />
          </a>
        }
      </div>
    </div>

    <!-- "View As" persona switcher strip — administrators only (see canViewAs). -->
    @if (canViewAs()) {
    <div
      class="px-6 py-2 bg-navy-deep border-t border-white/10 flex items-center gap-1 overflow-x-auto"
    >
      <span class="type-label text-white/40 mr-3 shrink-0">View As:</span>
      @for (chip of personaChips(); track chip.id) {
        <button
          type="button"
          (click)="selectPersona(chip.id)"
          [class]="
            chip.id === persona?.id
              ? 'whitespace-nowrap type-button px-3 py-1 bg-white text-navy font-semibold'
              : 'whitespace-nowrap type-button px-3 py-1 text-white/60 hover:text-white hover:bg-white/10'
          "
        >
          {{ chip.name }} <span class="opacity-70">· {{ chip.tier }}</span>
        </button>
      }
    </div>
    }
  </header>

  <!-- BODY — white sidebar + routed content -->
  <div class="flex flex-1 overflow-hidden">
    <aside class="w-52 shrink-0 bg-white border-r border-slate-200 flex flex-col py-5 overflow-y-auto print:hidden">
      <nav class="flex-1">
        @for (section of sections(); track section.title) {
          <div class="mb-5">
            @if (section.title) {
              <div class="type-label text-slate-500 px-3 mb-2">{{ section.title }}</div>
            }
            <div class="flex flex-col">
              @for (item of section.items; track item.label) {
                @if (item.route) {
                  <a
                    [routerLink]="item.route"
                    [queryParams]="item.query ?? null"
                    routerLinkActive="bg-slate-100 !border-navy text-slate-900 font-semibold"
                    class="flex items-center gap-3 px-3 py-2 type-body text-slate-600 border-l-2 border-transparent hover:bg-slate-50 hover:text-slate-900 transition-colors"
                  >
                    @if (item.icon === 'gem') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 shrink-0"><path d="M6 3h12l4 6-10 13L2 9Z" /><path d="M11 3 8 9l4 13 4-13-3-6" /><path d="M2 9h20" /></svg>
                    } @else {
                      <i class="pi {{ item.icon }} text-base w-4 text-center"></i>
                    }
                    <span>{{ item.label }}</span>
                    <!-- Backend-wiring marker. Blue = built and tried against a
                         real server; green = built but not tried yet; yellow =
                         still being worked on. Absent on pages still running on
                         seeded data. The blue/green split exists because written
                         code and working code look the same from the outside. -->
                    @if (item.wiring) {
                      <span
                        class="h-2 w-2 rounded-full shrink-0 {{ wiringDotClass(item.wiring) }}"
                        [title]="wiringTitle(item.wiring)"
                      ></span>
                    }
                    @if (item.badge) {
                      <span class="ml-auto number type-meta font-semibold bg-rose-600 text-white min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full shrink-0" [title]="item.badge + ' need attention'">{{ item.badge }}</span>
                    }
                  </a>
                } @else {
                  <span
                    class="flex items-center gap-3 px-3 py-2 type-body text-slate-300 border-l-2 border-transparent cursor-default"
                    title="Coming soon"
                  >
                    @if (item.icon === 'gem') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 shrink-0"><path d="M6 3h12l4 6-10 13L2 9Z" /><path d="M11 3 8 9l4 13 4-13-3-6" /><path d="M2 9h20" /></svg>
                    } @else {
                      <i class="pi {{ item.icon }} text-base w-4 text-center"></i>
                    }
                    <span>{{ item.label }}</span>
                  </span>
                }
              }
            </div>
          </div>
        }
      </nav>

      <div class="px-2 pt-3 mt-3 border-t border-slate-200">
        <button type="button" (click)="logout()" class="w-full text-left flex items-center gap-3 px-3 py-2 type-body text-slate-600 border-l-2 border-transparent hover:bg-rose-50 hover:text-rose-700 transition-colors">
          <i class="pi pi-sign-out text-base w-4 text-center"></i>
          <span>Log out</span>
        </button>
      </div>

      <div class="px-3 pt-4 mt-1 border-t border-slate-200 type-meta text-slate-400 leading-snug">
        DROS Beta v0.4 · Build 7b · <span class="text-navy-deep">Diamond Standard</span>
      </div>
    </aside>

    <main class="flex-1 overflow-y-auto p-6 print:overflow-visible print:p-0">
      <!-- Their access was rewritten and their token has not caught up. Nothing
           on screen reflects it until Keycloak issues a new one, so this asks for
           a sign-in rather than letting the change look like it did not save. -->
      @if (accessStale()) {
        <div
          class="mb-6 flex items-start gap-3 border border-amber-200 bg-amber-50 p-4 print:hidden"
          role="status"
        >
          <i class="pi pi-exclamation-triangle text-amber-600 mt-0.5"></i>
          <div class="flex-1">
            <div class="type-body font-semibold text-slate-900">Your access has changed</div>
            <p class="mt-1 type-body text-slate-600">
              Sign in again to pick it up. Until you do, this session still has the access it
              started with.
            </p>
          </div>
          <button
            type="button"
            (click)="logout()"
            class="h-control shrink-0 px-4 bg-navy text-white type-button font-semibold hover:bg-navy-deep transition-colors"
          >
            Sign in again
          </button>
        </div>
      }
      <router-outlet />
    </main>
  </div>
</div>
